import { useEffect, useState } from 'react'
import { useAccount } from 'wagmi'
import { parseUnits } from 'viem'
import { ArrowRight, AlertCircle, CheckCircle, Loader2, ExternalLink, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { useW2USDTBalance } from '@/hooks/useW2USDTBalance'
import { useTokenAllowance } from '@/hooks/useTokenAllowance'
import { useApproveToken } from '@/hooks/useApproveToken'
import { useLockTokens } from '@/hooks/useLockTokens'
import { useBridgeConfig } from '@/hooks/useBridgeConfig'
import { useFeeCalculator } from '@/hooks/useFeeCalculator'
import { useNetworkSwitch } from '@/hooks/useNetworkSwitch'
import { useBridgeStore } from '@/store/useBridgeStore'
import { shortenAddress, cn } from '@/lib/utils'
import { useLanguage } from '@/i18n'
import { api } from '@/lib/api'

const UNI2_EXPLORER = 'https://wer2scan.com'

export function BridgeForm() {
  const { address, isConnected } = useAccount()
  const { formData, setFormData, currentStep, setCurrentStep, reset } = useBridgeStore()
  const { balance, balanceDisplay, refetch: refetchBalance, isLoading: isLoadingBalance } = useW2USDTBalance()
  const { allowance, refetch: refetchAllowance } = useTokenAllowance()
  const { feePercentage, minAmountFormatted: minAmountRaw, maxAmountFormatted: maxAmountRaw, isPaused } = useBridgeConfig()
  const { isCorrectNetwork, switchToUni2, isPending: isSwitching } = useNetworkSwitch()
  const feeCalc = useFeeCalculator(formData.amount, feePercentage)
  const { t } = useLanguage()

  // Format min/max without decimals
  const minAmountFormatted = Math.floor(parseFloat(minAmountRaw || '0')).toString()
  const maxAmountFormatted = Math.floor(parseFloat(maxAmountRaw || '0')).toString()

  const {
    approve,
    isPending: isApproving,
    isSuccess: approveSuccess,
    reset: resetApprove,
  } = useApproveToken()

  const {
    lockTokens,
    isPending: isLocking,
    isSuccess: lockSuccess,
    hash: lockHash,
    reset: resetLock,
  } = useLockTokens()

  const amountWei = formData.amount ? parseUnits(formData.amount, 18) : 0n
  const needsApproval = allowance !== undefined && amountWei > allowance

  // Check if amount exceeds balance
  const exceedsBalance = balance !== undefined && amountWei > balance && formData.amount !== ''

  const isValidAmount =
    formData.amount &&
    parseFloat(formData.amount) >= parseFloat(minAmountFormatted) &&
    parseFloat(formData.amount) <= parseFloat(maxAmountFormatted) &&
    balance !== undefined &&
    amountWei <= balance

  const canSubmit = isValidAmount && !isPaused

  const handleApprove = async () => {
    try {
      await approve()
    } catch {
      toast.error(t('error'))
    }
  }

  const handleLock = async () => {
    if (!address) return
    try {
      await lockTokens({
        amount: formData.amount,
        expectedAmountOut: feeCalc.amountOut,
        bscRecipient: address, // Use the connected wallet address as BSC recipient
      })
    } catch {
      toast.error(t('error'))
    }
  }

  const handleRefreshBalance = () => {
    refetchBalance()
    toast.success(t('balance') + ' OK')
  }

  // Estado para mostrar el mensaje de paso completado
  const [showApproveSuccess, setShowApproveSuccess] = useState(false)

  useEffect(() => {
    if (approveSuccess) {
      // Mostrar mensaje claro de que ahora debe hacer el bridge
      toast.success('✓ ' + t('approve') + ' OK', { duration: 3000 })
      setShowApproveSuccess(true)
      refetchAllowance()
      resetApprove()

      // Ocultar el mensaje después de 5 segundos
      setTimeout(() => setShowApproveSuccess(false), 5000)
    }
  }, [approveSuccess])

  const [isSubmittingToQueue, setIsSubmittingToQueue] = useState(false)
  const [queueSubmitFailed, setQueueSubmitFailed] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successData, setSuccessData] = useState<{ amount: string; amountOut: string; txHash: string } | null>(null)
  const MAX_RETRIES = 3

  const submitToQueueWithRetry = async (txHash: string, userAddress: string, attempt: number = 1): Promise<boolean> => {
    try {
      await api.createBridgeRequest(txHash, userAddress)
      return true
    } catch (error: any) {
      console.error(`Failed to submit to queue (attempt ${attempt}/${MAX_RETRIES}):`, error)
      if (attempt < MAX_RETRIES) {
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt))
        return submitToQueueWithRetry(txHash, userAddress, attempt + 1)
      }
      return false
    }
  }

  useEffect(() => {
    const submitToQueue = async () => {
      if (lockSuccess && lockHash && address && !isSubmittingToQueue && !showSuccessModal) {
        setIsSubmittingToQueue(true)
        setQueueSubmitFailed(false)

        const success = await submitToQueueWithRetry(lockHash, address)

        if (success) {
          // Show success modal
          setSuccessData({
            amount: formData.amount,
            amountOut: feeCalc.amountOut,
            txHash: lockHash,
          })
          setShowSuccessModal(true)
        } else {
          // Lock is on-chain but API failed after retries
          setQueueSubmitFailed(true)
          setCurrentStep('success')
        }

        setIsSubmittingToQueue(false)
        refetchBalance()
      }
    }
    submitToQueue()
  }, [lockSuccess, lockHash, address])

  // Manual retry function for failed submissions
  const handleRetrySubmit = async () => {
    if (!lockHash || !address) return
    setIsSubmittingToQueue(true)

    const success = await submitToQueueWithRetry(lockHash, address)

    if (success) {
      toast.success('Solicitud registrada correctamente')
      setQueueSubmitFailed(false)
    } else {
      toast.error('No se pudo registrar. Contacta con soporte.')
    }
    setIsSubmittingToQueue(false)
  }

  const handleReset = () => {
    reset()
    resetApprove()
    resetLock()
    setShowSuccessModal(false)
    setSuccessData(null)
  }

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    setSuccessData(null)
    reset()
    resetApprove()
    resetLock()
  }

  // Success view
  if (currentStep === 'success' && lockHash) {
    return (
      <div className="card-glow p-4 sm:p-6 space-y-4">
        <div className="text-center">
          <div className={cn(
            "w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3",
            queueSubmitFailed ? "bg-yellow-500/20" : "bg-accent-500/20 animate-glow-pulse"
          )}>
            {queueSubmitFailed ? (
              <AlertCircle className="w-7 h-7 text-yellow-400" />
            ) : (
              <CheckCircle className="w-7 h-7 text-accent-400" />
            )}
          </div>
          <h3 className="heading text-xl text-white mb-2">{t('success')}!</h3>
          <p className="text-dark-400 text-sm">{t('pendingApproval')}</p>
        </div>

        {/* Warning if API sync failed */}
        {queueSubmitFailed && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-yellow-400 font-medium">Error de sincronización</p>
                <p className="text-yellow-400/80 text-xs mt-1">
                  Tu bridge se realizó correctamente en la blockchain, pero no se pudo registrar en el sistema.
                  Puedes intentar de nuevo o contactar con soporte.
                </p>
              </div>
            </div>
            <button
              onClick={handleRetrySubmit}
              disabled={isSubmittingToQueue}
              className="w-full btn-md bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmittingToQueue ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Registrando...</>
              ) : (
                <><RefreshCw className="w-4 h-4" /> Reintentar registro</>
              )}
            </button>
          </div>
        )}

        <div className="bg-dark-850/50 rounded-xl p-4 space-y-2 border border-dark-800/50 text-sm">
          <div className="flex justify-between">
            <span className="text-dark-400">{t('amount')}</span>
            <span className="font-mono">{formData.amount} W2USDT</span>
          </div>
          <div className="flex justify-between">
            <span className="text-dark-400">{t('youWillReceive')}</span>
            <span className="font-mono text-accent-400">{feeCalc.amountOut} USDT</span>
          </div>
          <div className="flex justify-between">
            <span className="text-dark-400">{t('destinationAddress')}</span>
            <span className="font-mono text-xs">{shortenAddress(address || '', 6)}</span>
          </div>
          <div className="pt-2 border-t border-dark-800/50 flex justify-between items-center">
            <span className="text-dark-400">{t('txHash')}</span>
            <a
              href={`${UNI2_EXPLORER}/uni2/tx/${lockHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-uni-400 hover:text-uni-300 flex items-center gap-1 text-xs"
            >
              {t('viewOnExplorer')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <button onClick={handleReset} className="btn-secondary btn-md w-full">
          {t('bridgeAction')}
        </button>
      </div>
    )
  }

  // Get button text based on state
  const getButtonContent = () => {
    if (!isConnected) {
      return <span className="text-dark-400 text-sm">{t('connectWallet')}</span>
    }

    if (!isCorrectNetwork) {
      return (
        <button onClick={switchToUni2} disabled={isSwitching} className="btn-secondary btn-md w-full">
          {isSwitching ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}</>
          ) : (
            'Cambiar a Uni2'
          )}
        </button>
      )
    }

    if (exceedsBalance) {
      return (
        <button disabled className="btn-lg w-full bg-red-500/20 border border-red-500/30 text-red-400 cursor-not-allowed">
          {t('insufficientBalance')}
        </button>
      )
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      return (
        <button disabled className="btn-secondary btn-lg w-full opacity-50 cursor-not-allowed">
          {t('enterValidAmount')}
        </button>
      )
    }

    if (parseFloat(formData.amount) < parseFloat(minAmountFormatted)) {
      return (
        <button disabled className="btn-secondary btn-lg w-full opacity-50 cursor-not-allowed">
          {t('amountBelowMin')}
        </button>
      )
    }

    if (parseFloat(formData.amount) > parseFloat(maxAmountFormatted)) {
      return (
        <button disabled className="btn-secondary btn-lg w-full opacity-50 cursor-not-allowed">
          {t('amountAboveMax')}
        </button>
      )
    }

    if (needsApproval) {
      return (
        <button onClick={handleApprove} disabled={!canSubmit || isApproving} className="btn-primary btn-lg w-full">
          {isApproving ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {t('approving')}</>
          ) : (
            t('approve')
          )}
        </button>
      )
    }

    return (
      <button
        onClick={handleLock}
        disabled={!canSubmit || isLocking}
        className={cn(
          "btn-primary btn-lg w-full",
          showApproveSuccess && "animate-pulse ring-2 ring-accent-400 ring-offset-2 ring-offset-dark-900"
        )}
      >
        {isLocking ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> {t('bridging')}</>
        ) : showApproveSuccess ? (
          <><CheckCircle className="w-4 h-4" /> Completar Bridge</>
        ) : (
          t('bridgeAction')
        )}
      </button>
    )
  }

  return (
    <div className="card-glow p-4 sm:p-6 space-y-4">
      {/* Header - Network Display */}
      <div className="flex items-center gap-2">
        <span className="text-dark-400 text-sm">{t('network')}:</span>
        <div className="flex items-center gap-2">
          <span className="network-badge">
            <span className="network-dot bg-uni-400"></span>
            Uni2
          </span>
          <ArrowRight className="w-4 h-4 text-uni-400" />
          <span className="network-badge">
            <span className="network-dot bg-yellow-400"></span>
            BSC
          </span>
        </div>
      </div>

      {/* Pause Warning */}
      {isPaused && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
          <p className="text-yellow-400 text-sm">{t('paused')}</p>
        </div>
      )}

      {/* Amount Input */}
      <div className="space-y-2">
        <div className="flex flex-wrap justify-between items-center gap-x-3 gap-y-1 text-sm">
          <label className="text-dark-300">{t('amount')}</label>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-dark-400 truncate">
              {t('balance')}: <span className={cn("font-mono", exceedsBalance ? "text-red-400" : "text-white")}>{balanceDisplay}</span>
            </span>
            <button
              onClick={handleRefreshBalance}
              disabled={isLoadingBalance}
              className="p-1 rounded-lg bg-dark-800/50 hover:bg-dark-700/50 text-dark-400 hover:text-uni-400 transition-all disabled:opacity-50 shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoadingBalance ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <div className="relative">
          <input
            type="number"
            placeholder="0"
            value={formData.amount}
            onChange={(e) => setFormData({ amount: e.target.value })}
            className={cn(
              "input text-lg font-mono pr-20",
              exceedsBalance && "border-red-500/50 focus:border-red-500 text-red-400"
            )}
            disabled={!isConnected || isPaused}
          />
          <button
            onClick={() => balance && setFormData({ amount: balanceDisplay })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-uni-400 text-sm font-semibold hover:text-uni-300 px-2 py-1 rounded-lg hover:bg-uni-500/10"
          >
            {t('max')}
          </button>
        </div>
        <p className="text-dark-500 text-xs">
          {t('min')}: {minAmountFormatted} • {t('max')}: {maxAmountFormatted} W2USDT
        </p>
      </div>

      {/* Destination */}
      <div className="bg-dark-850/50 rounded-xl p-3 border border-dark-800/50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-dark-400">{t('destinationAddress')}</span>
          <span className="font-mono text-white">
            {isConnected ? shortenAddress(address || '', 6) : '---'}
          </span>
        </div>
      </div>

      {/* Fee Preview */}
      {formData.amount && parseFloat(formData.amount) > 0 && (
        <div className="bg-dark-850/50 rounded-xl p-3 space-y-2 border border-dark-800/50 text-sm">
          <div className="flex justify-between">
            <span className="text-dark-400">{t('fee')} ({feePercentage}%)</span>
            <span className="text-dark-300 font-mono">{feeCalc.fee} W2USDT</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-dark-800/50">
            <span className="text-dark-300">{t('youWillReceive')}</span>
            <span className="font-semibold text-lg text-accent-400 font-mono">{feeCalc.amountOut} USDT</span>
          </div>
        </div>
      )}

      {/* Step indicator after approve success */}
      {showApproveSuccess && !needsApproval && (
        <div className="bg-accent-500/10 border border-accent-500/30 rounded-xl p-3 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="w-8 h-8 bg-accent-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-4 h-4 text-accent-400" />
          </div>
          <div className="flex-1">
            <p className="text-accent-400 text-sm font-medium">Paso 1 completado</p>
            <p className="text-accent-400/70 text-xs">Ahora pulsa el botón para completar el bridge</p>
          </div>
          <ArrowRight className="w-5 h-5 text-accent-400 animate-pulse" />
        </div>
      )}

      {/* Action Button */}
      <div>
        {getButtonContent()}
      </div>

      {/* Success Modal */}
      {showSuccessModal && successData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card-glow p-4 sm:p-6 max-w-sm w-full space-y-4 animate-in fade-in zoom-in-95 duration-200">
            {/* Success Icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-accent-500/20 rounded-full flex items-center justify-center animate-glow-pulse">
                <CheckCircle className="w-8 h-8 text-accent-400" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center">
              <h3 className="heading text-xl text-white mb-2">{t('success')}!</h3>
              <p className="text-dark-400 text-sm">
                {t('pendingApproval')}
              </p>
            </div>

            {/* Details */}
            <div className="bg-dark-850/50 rounded-xl p-4 space-y-3 border border-dark-800/50">
              <div className="flex justify-between text-sm">
                <span className="text-dark-400">{t('amount')}</span>
                <span className="font-mono text-white">{successData.amount} W2USDT</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-dark-400">{t('youWillReceive')}</span>
                <span className="font-mono text-accent-400 font-semibold">{successData.amountOut} USDT</span>
              </div>
              <div className="pt-2 border-t border-dark-800/50">
                <a
                  href={`${UNI2_EXPLORER}/uni2/tx/${successData.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-uni-400 hover:text-uni-300 flex items-center justify-center gap-1 text-sm"
                >
                  {t('viewOnExplorer')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-uni-500/10 border border-uni-500/20 rounded-xl p-3">
              <p className="text-uni-400 text-sm text-center">
                Tu solicitud será revisada en breve por nuestro equipo.
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={handleCloseSuccessModal}
              className="btn-primary btn-md w-full"
            >
              {t('accept')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
